import { Link2, Package, Plus, Search, Upload } from 'lucide-react';
import { useMemo, useState } from 'react';
import { PERMISSION } from '@obraplan/core/domain/access/permissions';
import { normalizeName } from '@obraplan/core/domain/shared/naming';
import { hasProvisionalCode, type Material } from '@obraplan/core/domain/schedule/material';
import type { ColumnMapping } from '@obraplan/core/domain/schedule/material-import';
import type { ImportPreview } from '@obraplan/core/application/use-cases/schedule/material-import-use-cases';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  TableWrapper,
} from '@/components/ui/table';
import { EmptyState, ErrorState, LoadingState } from '@/components/feedback/states';
import { CreateMaterialDialog } from '@/features/structure/ItemDialog';
import { LinkSkuDialog } from '@/features/materials/LinkSkuDialog';
import { MaterialImportDialog } from '@/features/materials/MaterialImportDialog';
import { useContainer, usePermission, useSessionState } from '@/features/session/use-session';
import { useCommand } from '@/lib/use-command';
import { useLive } from '@/lib/use-live';

/**
 * Catálogo de materiais — global, compartilhado por todas as obras (ADR-020), por isso em
 * Configurações. O código de compras vira o código aqui, o que mantém os dois sistemas
 * falando da mesma coisa (RN-010, RN-011).
 */
export function MaterialsPage() {
  const container = useContainer();
  const { session } = useSessionState();
  const canManage = usePermission(PERMISSION.MATERIALS_MANAGE);

  const [search, setSearch] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [linking, setLinking] = useState<Material | undefined>(undefined);
  const [preview, setPreview] = useState<ImportPreview | undefined>(undefined);
  // Os bytes ficam aqui para que trocar o mapeamento não exija reabrir o arquivo.
  const [fileBytes, setFileBytes] = useState<{ bytes: Uint8Array; name: string } | undefined>(
    undefined,
  );

  const catalog = useLive(
    () =>
      session === undefined ? Promise.resolve(undefined) : container.listMaterials.execute(session),
    [session?.userId],
  );

  const materials = useMemo(
    () => (catalog !== undefined && catalog.ok ? catalog.value.materials : []),
    [catalog],
  );
  const units = useMemo(
    () => (catalog !== undefined && catalog.ok ? catalog.value.units : []),
    [catalog],
  );
  const unitById = useMemo(() => new Map(units.map((unit) => [unit.id, unit])), [units]);

  const filtered = useMemo(() => {
    const term = normalizeName(search);
    if (term === '') return materials;
    return materials.filter(
      (material) =>
        normalizeName(material.name).includes(term) ||
        normalizeName(material.code).includes(term) ||
        normalizeName(material.category ?? '').includes(term),
    );
  }, [materials, search]);

  const [createMaterial, createState] = useCommand(
    (input: { code: string; name: string; unitId: string; category: string | undefined }) =>
      container.createMaterial.execute(input, session!),
    {
      successMessage: 'Material criado.',
      silent: true,
      onSuccess: () => setCreateOpen(false),
    },
  );

  // RN-347 — o código provisório do orçamento vira o SKU por decisão de alguém, aqui.
  const [linkSku, linkState] = useCommand(
    (input: { materialId: string; sku: string }) =>
      container.linkMaterialToSku.execute(input, session!),
    {
      successMessage: (material) => `Material vinculado ao SKU ${material.code}.`,
      silent: true,
      onSuccess: () => setLinking(undefined),
    },
  );

  const provisional = useMemo(
    () => materials.filter((material) => hasProvisionalCode(material)).length,
    [materials],
  );

  const [runPreview, previewState] = useCommand(
    (input: {
      bytes: Uint8Array;
      fileName: string;
      mapping?: ColumnMapping;
      fallbackUnitId?: string | undefined;
    }) => container.previewMaterialImport.execute(input, session!),
    { silent: true, onSuccess: (value) => setPreview(value) },
  );

  const [confirmImport, confirmState] = useCommand(
    (updateExisting: boolean) =>
      container.confirmMaterialImport.execute(
        { rows: preview?.rows ?? [], updateExisting, fileName: preview?.fileName ?? '' },
        session!,
      ),
    {
      successMessage: (result) =>
        `${String(result.created)} materiais criados · ${String(result.updated)} atualizados.`,
      silent: true,
      onSuccess: () => {
        setImportOpen(false);
        setPreview(undefined);
        setFileBytes(undefined);
      },
    },
  );

  const handleFile = (file: File): void => {
    void file.arrayBuffer().then((buffer) => {
      const bytes = new Uint8Array(buffer);
      setFileBytes({ bytes, name: file.name });
      void runPreview({ bytes, fileName: file.name });
    });
  };

  const handleRemap = (mapping: ColumnMapping, fallbackUnitId: string | undefined): void => {
    if (fileBytes === undefined) return;
    void runPreview({ bytes: fileBytes.bytes, fileName: fileBytes.name, mapping, fallbackUnitId });
  };

  if (catalog === undefined) return <LoadingState label="Carregando o catálogo…" />;
  if (!catalog.ok) return <ErrorState message={catalog.error.message} code={catalog.error.code} />;

  return (
    <div className="flex flex-col gap-6">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">Materiais</h1>
          <p className="text-(--color-content-muted)">Catálogo compartilhado por todas as obras.</p>
        </div>
        {canManage && (
          <div className="flex flex-wrap gap-2">
            <Button variant="secondary" onClick={() => setCreateOpen(true)}>
              <Plus aria-hidden="true" />
              Novo material
            </Button>
            <Button variant="primary" onClick={() => setImportOpen(true)}>
              <Upload aria-hidden="true" />
              Importar do sistema de compras
            </Button>
          </div>
        )}
      </header>

      <Card>
        <CardHeader className="flex-row items-center justify-between gap-3">
          <div>
            <CardTitle>{materials.length} materiais cadastrados</CardTitle>
            <CardDescription>
              O código é único e casa com o cadastro de compras.
              {provisional > 0 &&
                ` ${String(provisional)} com código provisório do orçamento (ORC-…), que não vira requisição até ser vinculado ao SKU.`}
            </CardDescription>
          </div>
          <div className="relative flex items-center">
            <Search
              className="pointer-events-none absolute left-2.5 size-4 text-(--color-content-muted)"
              aria-hidden="true"
            />
            <Input
              aria-label="Buscar material"
              className="w-56 pl-8"
              placeholder="Buscar por código, nome…"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {filtered.length === 0 ? (
            <EmptyState
              title={materials.length === 0 ? 'Catálogo vazio' : 'Nenhum material encontrado'}
              description={
                materials.length === 0
                  ? 'Importe o cadastro de itens do seu sistema de compras ou cadastre um material.'
                  : 'Nenhum material corresponde à busca.'
              }
              icon={<Package className="size-8" />}
              {...(canManage && materials.length === 0
                ? { action: { label: 'Importar catálogo', onClick: () => setImportOpen(true) } }
                : {})}
            />
          ) : (
            <TableWrapper>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Código</TableHead>
                    <TableHead>Material</TableHead>
                    <TableHead>Unidade</TableHead>
                    <TableHead>Categoria</TableHead>
                    {canManage && provisional > 0 && (
                      <TableHead>
                        <span className="sr-only">Ações</span>
                      </TableHead>
                    )}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.map((material) => (
                    <TableRow key={material.id}>
                      <TableCell className="font-mono text-xs">
                        {material.code}
                        {hasProvisionalCode(material) && (
                          <Badge tone="warn" className="ml-2 font-sans">
                            Provisório
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="font-medium">{material.name}</TableCell>
                      <TableCell>{unitById.get(material.unitId)?.name ?? '—'}</TableCell>
                      <TableCell className="text-(--color-content-muted)">
                        {material.category === undefined ? (
                          '—'
                        ) : (
                          <Badge tone="neutral">{material.category}</Badge>
                        )}
                      </TableCell>
                      {canManage && provisional > 0 && (
                        <TableCell className="text-right">
                          {hasProvisionalCode(material) && (
                            <Button
                              variant="secondary"
                              size="sm"
                              onClick={() => setLinking(material)}
                            >
                              <Link2 aria-hidden="true" />
                              Vincular ao SKU
                            </Button>
                          )}
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableWrapper>
          )}
        </CardContent>
      </Card>

      <CreateMaterialDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        units={units}
        onSubmit={(input) => void createMaterial(input)}
        isPending={createState.isPending}
        errorMessage={createState.error?.message}
      />

      <LinkSkuDialog
        material={linking}
        onOpenChange={(open) => {
          if (!open) setLinking(undefined);
        }}
        onSubmit={(input) => void linkSku(input)}
        isPending={linkState.isPending}
        errorMessage={linkState.error?.message}
      />

      <MaterialImportDialog
        open={importOpen}
        onOpenChange={(open) => {
          setImportOpen(open);
          if (!open) {
            setPreview(undefined);
            setFileBytes(undefined);
          }
        }}
        preview={preview}
        onFile={handleFile}
        onRemap={handleRemap}
        onConfirm={(updateExisting) => void confirmImport(updateExisting)}
        isPending={previewState.isPending || confirmState.isPending}
        errorMessage={previewState.error?.message ?? confirmState.error?.message}
      />
    </div>
  );
}
